import React, { useEffect, useState } from 'react';
import toastError from "../../errors/toastError";
import { 
  Button, 
  Typography, 
  Box, 
  Card, 
  CardContent,
  Chip,
  useTheme
} from "@material-ui/core";
import { LocationOn, OpenInNew, Map } from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";

// 🎯 Importar o componente MinimalistMap
import MinimalistMap from "./MinimalistMap";

// 🎨 Estilos modernos para LocationPreview
const useLocationStyles = makeStyles((theme) => ({
  locationCard: {
    minWidth: '280px',
    maxWidth: '350px',
    borderRadius: '16px',
    overflow: 'hidden',
    boxShadow: '0 2px 12px rgba(0, 0, 0, 0.1)',
    transition: 'all 0.3s ease',
    cursor: 'pointer',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
    }
  },
  mapPreview: {
    position: 'relative',
    height: '140px',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.palette.grey[100],
    overflow: 'hidden',
  },
  mapImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    transition: 'transform 0.3s ease',
    '&:hover': {
      transform: 'scale(1.05)',
    }
  },
  mapOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.3), rgba(33, 150, 243, 0.3))',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    opacity: 0,
    transition: 'opacity 0.3s ease',
    '&:hover': {
      opacity: 1,
    }
  },
  pinIcon: {
    color: '#e53935',
    fontSize: '3rem',
    filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3))',
    animation: '$bounce 2s infinite',
  },
  '@keyframes bounce': {
    '0%, 20%, 50%, 80%, 100%': {
      transform: 'translateY(0)',
    },
    '40%': {
      transform: 'translateY(-8px)',
    },
    '60%': {
      transform: 'translateY(-4px)',
    },
  },
  contentArea: {
    padding: '16px',
    background: 'linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%)',
  },
  coordinatesContainer: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '12px',
    gap: '8px',
  },
  coordinatesChip: {
    backgroundColor: theme.palette.primary.light,
    color: theme.palette.primary.contrastText,
    fontFamily: 'monospace',
    fontSize: '0.75rem',
    fontWeight: 600,
    borderRadius: '12px',
    '& .MuiChip-icon': {
      color: 'inherit',
      fontSize: '1rem',
    }
  },
  description: {
    marginBottom: '16px',
    color: theme.palette.text.secondary,
    lineHeight: 1.4,
    fontSize: '0.875rem',
  },
  actionButton: {
    borderRadius: '12px',
    textTransform: 'none',
    fontWeight: 600,
    padding: '10px 20px',
    background: 'linear-gradient(135deg, #4caf50 0%, #45a049 100%)',
    color: 'white',
    boxShadow: '0 4px 12px rgba(76, 175, 80, 0.3)',
    transition: 'all 0.3s ease',
    '&:hover': {
      background: 'linear-gradient(135deg, #45a049 0%, #388e3c 100%)',
      transform: 'translateY(-1px)',
      boxShadow: '0 6px 20px rgba(76, 175, 80, 0.4)',
    },
    '&:disabled': {
      background: theme.palette.grey[300],
      color: theme.palette.grey[600],
      boxShadow: 'none',
    }
  },
  fallbackContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '140px',
    backgroundColor: theme.palette.grey[100],
    color: theme.palette.grey[600],
  },
}));

const LocationPreview = ({ image, link, description }) => {
  const classes = useLocationStyles();
  const theme = useTheme();
  const [imageError, setImageError] = useState(false);

  useEffect(() => { 
    setImageError(false);
  }, [image]);

  // 🗺️ Extrair coordenadas do description (formato: "lat,lng")
  const extractCoordinates = () => {
    if (!description) return null;
    
    // Tentar extrair coordenadas do description
    const coordMatch = description.match(/-?\d+\.?\d*,-?\d+\.?\d*/);
    if (coordMatch) {
      const [lat, lng] = coordMatch[0].split(',');
      return {
        lat: parseFloat(lat).toFixed(6),
        lng: parseFloat(lng).toFixed(6)
      };
    }
    
    // Fallback - tentar extrair do link do Google Maps
    if (link) {
      const linkCoordMatch = link.match(/@(-?\d+\.?\d*),(-?\d+\.?\d*)/);
      if (linkCoordMatch) {
        return {
          lat: parseFloat(linkCoordMatch[1]).toFixed(6),
          lng: parseFloat(linkCoordMatch[2]).toFixed(6)
        };
      }
    }
    
    return null;
  };

  const coordinates = extractCoordinates();

  const handleLocation = async () => {
    try {
      if (link) {
        window.open(link, '_blank');
      }
    } catch (err) {
      toastError(err);
    }
  };

  const handleImageError = () => {
    setImageError(true);
  };

  return (
    <Card className={classes.locationCard} onClick={handleLocation}>
      {/* 🗺️ Preview do Mapa SVG Minimalista */}
      <div className={classes.mapPreview}>
        {image && !imageError ? (
          <>
            <img 
              src={image} 
              alt="Localização no mapa" 
              className={classes.mapImage}
              onError={handleImageError}
            />
            <div className={classes.mapOverlay}>
              <LocationOn className={classes.pinIcon} />
            </div>
          </>
        ) : (
          /* 🎨 Usar o mapa SVG minimalista como fallback */
          <MinimalistMap 
            coordinates={coordinates} 
            onClick={handleLocation}
          />
        )}
      </div>

      {/* 📋 Conteúdo */}
      <CardContent className={classes.contentArea}>
        {/* 📍 Coordenadas */}
        {coordinates && (
          <div className={classes.coordinatesContainer}>
            <Chip
              icon={<LocationOn />}
              label={`${coordinates.lat}, ${coordinates.lng}`}
              className={classes.coordinatesChip}
              size="small"
            />
          </div>
        )}

        {/* 📝 Descrição */}
        {description && description !== `${coordinates?.lat},${coordinates?.lng}` && (
          <Typography className={classes.description}>
            {description.replace(/\\n/g, '\n')}
          </Typography>
        )}

        {/* 🔗 Botão de Ação */}
        <Button
          fullWidth
          className={classes.actionButton}
          onClick={handleLocation}
          disabled={!link}
          startIcon={<OpenInNew />}
        >
          Abrir no Google Maps
        </Button>
      </CardContent>
    </Card>
  );
};

export default LocationPreview;